import { useMemo, useRef, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Pencil, PowerOff, Search, ShieldCheck, Trash2, Upload } from 'lucide-react'
import Button from '@/components/button'
import ConfirmDialog from '@/components/confirm-dialog'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import DefaultModal from '@/components/default-modal'
import EmptyState from '@/components/empty-state'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import Pagination from '@/components/pagination'
import Select from '@/components/select'
import Skeleton from '@/components/skeleton'
import StatusBadge from '@/components/status-badge'
import {
  useAlterarSituacaoProcedimentoTabelaMutation,
  useAtualizarCredencialMutation,
  useAtualizarPlanoMutation,
  useBaixarTabelaPropriaMutation,
  useCriarPlanoMutation,
  useExcluirPlanoMutation,
  useImportarTabelaPropriaMutation,
} from '@/http/mutations/credenciais'
import { usePlanosQuery, useTabelaPropriaQuery } from '@/http/queries/credenciais'
import { comCondutaPadrao } from '@/types/credencial'
import type { Credencial, PlanoCredencial, ProcedimentoTabelaPropria } from '@/types/credencial'
import { TABELA_CREDENCIAL } from '@/types/domain'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { formatMoeda } from '@/utility/Utils'
import styles from '../style.module.css'

const schemaTabela = yup.object({
  tabela: yup.string().required('Selecione a tabela'),
})

type TabelaFormValues = yup.InferType<typeof schemaTabela>

const TabelaSelecaoForm = ({ credencial }: { credencial: Credencial }) => {
  const { can } = useAuth()
  const podeEditar = can(ACAO.EditarCredencial)
  const [pendente, setPendente] = useState<Credencial | null>(null)

  const { formState, handleSubmit, register, reset } = useForm<TabelaFormValues>({
    resolver: yupResolver(schemaTabela),
    defaultValues: { tabela: credencial.tabela ?? TABELA_CREDENCIAL.Tuss },
  })

  const atualizar = useAtualizarCredencialMutation(() => {
    if (pendente) reset({ tabela: pendente.tabela ?? TABELA_CREDENCIAL.Tuss })
    setPendente(null)
  })

  const onSubmit = (dados: TabelaFormValues) => setPendente({ ...credencial, tabela: dados.tabela })

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField label="Tabela de procedimentos" htmlFor="tabela">
          <Select
            id="tabela"
            options={[
              { value: TABELA_CREDENCIAL.Tuss, label: 'TUSS' },
              { value: TABELA_CREDENCIAL.Propria, label: 'Própria' },
            ]}
            disabled={!podeEditar}
            {...register('tabela')}
          />
        </FormField>

        {podeEditar ? (
          <div className={styles.actions}>
            <Button type="submit" disabled={!formState.isDirty}>
              Salvar alterações
            </Button>
          </div>
        ) : null}
      </form>

      <ConfirmDialog
        isOpen={Boolean(pendente)}
        toggle={() => setPendente(null)}
        title="Confirmar alteração da tabela"
        message="Trocar a tabela de procedimentos muda como as autorizações dessa credencial calculam valor e código, com efeito imediato. Deseja continuar?"
        confirmLabel="Salvar alterações"
        tone="warning"
        isLoading={atualizar.isPending}
        onConfirm={() => pendente && atualizar.mutate(comCondutaPadrao(pendente))}
      />
    </>
  )
}

const schemaPlano = yup.object({
  descricao: yup.string().trim().required('Preencha a descrição').max(250, 'No máximo 250 caracteres'),
  codigo: yup.string().defined().nullable().default('').max(30, 'No máximo 30 caracteres'),
})

type PlanoFormValues = yup.InferType<typeof schemaPlano>

const TAMANHO_PAGINA_PLANOS = 20

const PlanosSection = ({ credencial }: { credencial: Credencial }) => {
  const { can } = useAuth()
  const podeEditar = can(ACAO.EditarCredencial)
  const credencialId = credencial.id ?? ''

  const [pageNumber, setPageNumber] = useState(1)
  const filtro = useMemo(() => ({ pageNumber, pageSize: TAMANHO_PAGINA_PLANOS }), [pageNumber])
  const planosQuery = usePlanosQuery(credencialId, filtro)
  const [editando, setEditando] = useState<PlanoCredencial | null>(null)
  const [modalAberto, setModalAberto] = useState(false)
  const [excluindo, setExcluindo] = useState<PlanoCredencial | null>(null)

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<PlanoFormValues>({
    resolver: yupResolver(schemaPlano),
    defaultValues: { descricao: '', codigo: '' },
  })

  const fecharModal = () => {
    setEditando(null)
    setModalAberto(false)
    reset({ descricao: '', codigo: '' })
  }

  const criar = useCriarPlanoMutation(credencialId, fecharModal)
  const atualizar = useAtualizarPlanoMutation(credencialId, fecharModal)
  const excluir = useExcluirPlanoMutation(credencialId, () => setExcluindo(null))

  const iniciarEdicao = (plano: PlanoCredencial) => {
    setEditando(plano)
    setModalAberto(true)
    reset({ descricao: plano.descricao ?? '', codigo: plano.codigo ?? '' })
  }

  const iniciarCriacao = () => {
    setEditando(null)
    setModalAberto(true)
    reset({ descricao: '', codigo: '' })
  }

  const onSubmit = (dados: PlanoFormValues) => {
    const body = { descricao: dados.descricao, codigo: dados.codigo ? dados.codigo.trim() : null }
    if (editando) {
      atualizar.mutate({ id: editando.id, body })
    } else {
      criar.mutate(body)
    }
  }

  if (planosQuery.isPending) {
    return <Skeleton variant="text" count={4} />
  }

  const planos = planosQuery.data?.items ?? []

  return (
    <div className={styles.sections}>
      {podeEditar ? (
        <div className={styles.toolbar}>
          <Button type="button" variant="outline" size="sm" onClick={iniciarCriacao}>
            Adicionar plano
          </Button>
        </div>
      ) : null}

      {planos.length ? (
        <ul className={styles.itemList}>
          {planos.map((plano) => (
            <li key={plano.id} className={styles.itemRow}>
              <span>
                {plano.descricao}
                {plano.codigo ? ` — ${plano.codigo}` : ''}
              </span>
              {podeEditar ? (
                <span className={styles.itemRowActions}>
                  <button
                    type="button"
                    className={styles.itemRemover}
                    aria-label={`Editar ${plano.descricao}`}
                    onClick={() => iniciarEdicao(plano)}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    className={styles.itemRemover}
                    aria-label={`Excluir ${plano.descricao}`}
                    onClick={() => setExcluindo(plano)}
                  >
                    <Trash2 size={15} />
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Nenhum plano cadastrado ainda" />
      )}

      {planosQuery.data ? (
        <Pagination
          pageIndex={planosQuery.data.pageIndex}
          totalPages={planosQuery.data.totalPages}
          totalItems={planosQuery.data.totalItems}
          onPageChange={setPageNumber}
        />
      ) : null}

      <DefaultModal
        isOpen={modalAberto}
        toggle={fecharModal}
        title={editando ? 'Editar plano' : 'Adicionar plano'}
        footer={
          <>
            <Button type="button" variant="outline" onClick={fecharModal}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="formPlano"
              isLoading={criar.isPending || atualizar.isPending}
            >
              {editando ? 'Salvar edição' : 'Adicionar plano'}
            </Button>
          </>
        }
      >
        <form id="formPlano" className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormField label="Descrição" htmlFor="planoDescricao" required error={errors.descricao?.message}>
            <Input id="planoDescricao" invalid={Boolean(errors.descricao)} {...register('descricao')} />
          </FormField>

          <FormField label="Código" htmlFor="planoCodigo" error={errors.codigo?.message}>
            <Input id="planoCodigo" invalid={Boolean(errors.codigo)} {...register('codigo')} />
          </FormField>
        </form>
      </DefaultModal>

      <ConfirmDialog
        isOpen={Boolean(excluindo)}
        toggle={() => setExcluindo(null)}
        title="Excluir plano"
        message={`Tem certeza que deseja excluir o plano "${excluindo?.descricao ?? ''}"? Essa ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        tone="danger"
        isDestructive
        isLoading={excluir.isPending}
        onConfirm={() => excluindo && excluir.mutate(excluindo.id)}
      />
    </div>
  )
}

const lerArquivoComoBase64 = (arquivo: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const leitor = new FileReader()
    leitor.onload = () => {
      const resultado = leitor.result as string
      const virgula = resultado.indexOf(',')
      resolve(virgula >= 0 ? resultado.slice(virgula + 1) : resultado)
    }
    leitor.onerror = () => reject(leitor.error ?? new Error('Falha ao ler o arquivo'))
    leitor.readAsDataURL(arquivo)
  })

const TabelaPropriaSection = ({ credencial }: { credencial: Credencial }) => {
  const { can } = useAuth()
  const podeEditar = can(ACAO.EditarCredencial)
  const credencialId = credencial.id ?? ''
  const usaTabelaPropria = credencial.tabela === TABELA_CREDENCIAL.Propria

  const [busca, setBusca] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [arquivoPendente, setArquivoPendente] = useState<File | null>(null)
  const inputArquivoRef = useRef<HTMLInputElement>(null)

  const buscaDebounced = useDebouncedValue(busca)
  const filtro = useMemo(
    () => ({ busca: buscaDebounced, pageNumber, pageSize }),
    [buscaDebounced, pageNumber, pageSize],
  )

  const tabelaQuery = useTabelaPropriaQuery(usaTabelaPropria ? credencialId : undefined, filtro)

  const importar = useImportarTabelaPropriaMutation(credencialId, () => {
    setArquivoPendente(null)
    setPageNumber(1)
    if (inputArquivoRef.current) inputArquivoRef.current.value = ''
  })
  const alterarSituacao = useAlterarSituacaoProcedimentoTabelaMutation(credencialId)
  const baixar = useBaixarTabelaPropriaMutation(
    credencialId,
    `tabela_${credencial.nome ?? credencialId}.xlsx`,
  )

  const [confirmandoProcedimento, setConfirmandoProcedimento] = useState<{
    id: string
    ativo: boolean
  } | null>(null)

  if (!usaTabelaPropria) {
    return (
      <EmptyState
        title="Essa credencial usa a tabela TUSS"
        description="A tabela própria só existe para credenciais configuradas para usar uma tabela de procedimentos própria."
      />
    )
  }

  const columns: Column<ProcedimentoTabelaPropria>[] = [
    { key: 'descricao', header: 'Descrição', render: (item) => item.descricao },
    { key: 'codigo', header: 'Código', render: (item) => item.codigo },
    { key: 'codigoTuss', header: 'Código TUSS', render: (item) => item.codigoTuss ?? '-' },
    { key: 'valor', header: 'Valor', render: (item) => formatMoeda(item.valor) },
    { key: 'desmembra', header: 'Desmembra', render: (item) => (item.desmembra ? 'Sim' : 'Não') },
    {
      key: 'situacao',
      header: 'Situação',
      render: (item) => (
        <StatusBadge label={item.ativo ? 'Ativo' : 'Inativo'} tone={item.ativo ? 'success' : 'neutral'} />
      ),
    },
    {
      key: 'acoes',
      header: '',
      align: 'right',
      render: (item) =>
        podeEditar ? (
          <Button
            variant={item.ativo ? 'danger' : 'success'}
            size="sm"
            iconLeft={item.ativo ? <PowerOff size={14} /> : <ShieldCheck size={14} />}
            onClick={() => setConfirmandoProcedimento({ id: item.id, ativo: !item.ativo })}
          >
            {item.ativo ? 'Inativar' : 'Ativar'}
          </Button>
        ) : null,
    },
  ]

  return (
    <div className={styles.sections}>
      <DataTable
        columns={columns}
        page={tabelaQuery.data}
        getRowKey={(item) => item.id}
        isLoading={tabelaQuery.isPending}
        error={tabelaQuery.error}
        onRetry={() => tabelaQuery.refetch()}
        onPageChange={setPageNumber}
        pageSize={pageSize}
        onPageSizeChange={(novoTamanho) => {
          setPageSize(novoTamanho)
          setPageNumber(1)
        }}
        emptyTitle="Nenhum procedimento cadastrado ainda"
        toolbar={
          <>
            <Input
              value={busca}
              onChange={(event) => {
                setBusca(event.target.value)
                setPageNumber(1)
              }}
              placeholder="Preencha a descrição ou o código"
              iconLeft={<Search size={15} />}
            />
            <div className={styles.itemRowActions}>
              {podeEditar ? (
                <>
                  <input
                    ref={inputArquivoRef}
                    type="file"
                    accept=".csv,.xlsx"
                    hidden
                    onChange={(event) => setArquivoPendente(event.target.files?.[0] ?? null)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    iconLeft={<Upload size={14} />}
                    isLoading={importar.isPending}
                    onClick={() => inputArquivoRef.current?.click()}
                  >
                    Importar tabela
                  </Button>
                </>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                isLoading={baixar.isPending}
                onClick={() => baixar.mutate()}
              >
                Baixar tabela
              </Button>
            </div>
          </>
        }
      />

      <ConfirmDialog
        isOpen={Boolean(arquivoPendente)}
        toggle={() => setArquivoPendente(null)}
        title="Substituir tabela de procedimentos"
        message="Importar um arquivo apaga todos os procedimentos atuais e coloca os do arquivo no lugar. Essa ação não pode ser desfeita."
        confirmLabel="Substituir tabela"
        tone="danger"
        isDestructive
        isLoading={importar.isPending}
        onConfirm={async () => {
          if (!arquivoPendente) return
          const base64 = await lerArquivoComoBase64(arquivoPendente)
          importar.mutate({ base64, name: arquivoPendente.name, type: arquivoPendente.type })
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(confirmandoProcedimento)}
        toggle={() => setConfirmandoProcedimento(null)}
        title={confirmandoProcedimento?.ativo ? 'Ativar procedimento' : 'Inativar procedimento'}
        message={
          confirmandoProcedimento?.ativo
            ? 'O procedimento volta a ser aceito nas autorizações por essa credencial.'
            : 'O procedimento deixa de ser aceito nas autorizações por essa credencial.'
        }
        confirmLabel={confirmandoProcedimento?.ativo ? 'Ativar' : 'Inativar'}
        tone={confirmandoProcedimento?.ativo ? 'success' : 'danger'}
        isLoading={alterarSituacao.isPending}
        onConfirm={() => {
          if (!confirmandoProcedimento) return
          alterarSituacao.mutate(confirmandoProcedimento)
          setConfirmandoProcedimento(null)
        }}
      />
    </div>
  )
}

const PlanosTabelaTab = ({ credencial }: { credencial: Credencial }) => (
  <div className={styles.sections}>
    <TabelaSelecaoForm credencial={credencial} />
    <PlanosSection credencial={credencial} />
    <TabelaPropriaSection credencial={credencial} />
  </div>
)

export default PlanosTabelaTab
